import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import WStoreDiscoverItem from 'containers/WStorePage/WStoreDiscoverItem';
import { isItemAvailable } from 'containers/WStorePage/WStoreUtils';

const DiscoverItemList = ({
  isDesktop,
  patronAssets,
  wstoreItems,
  onSelectItem
}) => {
  const patronSapphireBalance = patronAssets.sapphires;

  const availableItemsWithSapphireCost = useMemo(
    () =>
      wstoreItems.filter(item => item.sapphireCost && isItemAvailable(item)),
    [wstoreItems]
  );

  const purchasableItems = useMemo(
    () =>
      availableItemsWithSapphireCost.filter(
        item => patronSapphireBalance >= item.sapphireCost
      ),
    [availableItemsWithSapphireCost, patronSapphireBalance]
  );

  const uniqueAffordableItems = [];
  const classCodesSet = new Set();
  const maxItems = isDesktop ? 3 : 1;
  for (const item of purchasableItems) {
    if (!classCodesSet.has(item.classCode)) {
      uniqueAffordableItems.push(item);
      classCodesSet.add(item.classCode);
    }
    if (uniqueAffordableItems.length >= maxItems) {
      break;
    }
  }

  let sapphiresNeededForNextPurchase = 0;
  let nextAffordableItems = [];
  if (!uniqueAffordableItems.length) {
    const groupBy = (array, key) => {
      return array.reduce((result, currentValue) => {
        (result[currentValue[key]] = result[currentValue[key]] || []).push(
          currentValue
        );
        return result;
      }, {});
    };

    const filterUniqueByClassCode = items => {
      return items.filter(item => {
        if (!classCodesSet.has(item.classCode)) {
          classCodesSet.add(item.classCode);
          return true;
        }
        return false;
      });
    };

    const getNextAffordableItems = items => {
      const sortedItems = items.sort((a, b) => a.sapphireCost - b.sapphireCost);
      const groupedByCost = groupBy(sortedItems, 'sapphireCost');

      for (const cost in groupedByCost) {
        const uniqueItems = filterUniqueByClassCode(groupedByCost[cost]);
        if (uniqueItems.length > 0) {
          return isDesktop ? uniqueItems.slice(0, 3) : uniqueItems.slice(0, 1);
        }
      }

      return [];
    };
    nextAffordableItems = [
      ...getNextAffordableItems(availableItemsWithSapphireCost)
    ];
    if (nextAffordableItems.length) {
      sapphiresNeededForNextPurchase =
        nextAffordableItems[0].sapphireCost - patronSapphireBalance;
    }
  }

  const renderWStoreItem = (filteredItem, locked = false) => (
    <WStoreDiscoverItem
      item={filteredItem}
      onSelect={onSelectItem}
      key={filteredItem.itemId}
      locked={locked}
    />
  );

  if (uniqueAffordableItems.length) {
    return (
      <>
        <div className="discover-title">Redeem today!</div>
        <div className="discover-subtitle">
          Check out what you can get with your {patronSapphireBalance}{' '}
          sapphires.
        </div>
        <div className="discover-items-container">
          {uniqueAffordableItems.map(item => renderWStoreItem(item))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="discover-title">
        {`Only ${sapphiresNeededForNextPurchase} Sapphire${
          sapphiresNeededForNextPurchase === 1 ? '' : 's'
        } to go!`}
      </div>
      <div className="discover-subtitle">
        Keep playing and earning to access great rewards.
        <Link to="/slots" className="games-link">
          Play now
        </Link>
      </div>
      <div className="discover-items-container">
        {nextAffordableItems.map(item => renderWStoreItem(item, true))}
      </div>
    </>
  );
};

DiscoverItemList.propTypes = {
  isDesktop: PropTypes.bool,
  patronAssets: PropTypes.any,
  onSelectItem: PropTypes.func,
  wstoreItems: PropTypes.arrayOf(PropTypes.object)
};

export default DiscoverItemList;
